/**
 * Passaggio alla nuova Dashboard (design del 22/10/2026, tavole 7-8 di
 * https://claude.ai/artifact/BYAwMMC3Spa7fxDM7pEjK8). Ognuno sceglie: nessuno si ritrova la pagina
 * cambiata senza volerlo. L'admin puo' mettere la nuova (o quella di prima) per tutti.
 * Modulo puro, test in __tests__/rollout.test.ts.
 */

/** Scelta dell'azienda, in `layout_config.rollout` della predefinita di azienda. */
export interface OrgRollout {
  mode?: 'choose' | 'new' | 'classic';
  /** Con mode 'new': fino a questo giorno (YYYY-MM-DD) si puo' ancora tornare a quella di prima. */
  back_until?: string;
  /** "Ricordalo a chi non l'ha provata": l'invito ricompare a chi l'aveva rimandato prima di questa data. */
  reminder_at?: string;
}

/** Stato della persona, in `dashboard_prefs` del profilo. */
export interface UserSwitchPrefs {
  version?: 'new' | 'classic';
  /** Quando ha scelto la nuova (inizio della prova). */
  version_at?: string;
  invite_snoozed_until?: string;
  invite_snoozes?: number;
  /** Ha gia' risposto (o chiuso) la domanda dei tre giorni. */
  checkin_done?: boolean;
}

export interface SwitchState {
  version: 'new' | 'classic';
  /** Mostrare l'invito nella Dashboard di prima: grande, piccolo (dopo 3 "Piu' tardi") o niente. */
  invite: 'full' | 'compact' | 'none';
  /** Si puo' tornare alla Dashboard di prima. */
  canGoBack: boolean;
  /** Primi 7 giorni dalla scelta: etichetta "in prova" nella testata. */
  inTrial: boolean;
  /** Dopo 3 giorni, una volta sola: "Ti trovi bene?". */
  showCheckin: boolean;
}

const DAY = 86_400_000;
export const TRIAL_DAYS = 7;
export const CHECKIN_DAYS = 3;
export const SNOOZE_DAYS = 7;
export const SNOOZES_BEFORE_COMPACT = 3;

const day = (d: Date) => d.toISOString().slice(0, 10);

export function resolveSwitch(user: UserSwitchPrefs | undefined, org: OrgRollout | undefined, now: Date = new Date()): SwitchState {
  const mode = org?.mode ?? 'choose';
  const u = user ?? {};
  if (mode === 'classic') return { version: 'classic', invite: 'none', canGoBack: false, inTrial: false, showCheckin: false };

  const backOpen = mode === 'choose' || !org?.back_until || day(now) <= org.back_until;
  let version: 'new' | 'classic';
  if (mode === 'new') version = u.version === 'classic' && backOpen ? 'classic' : 'new';
  else version = u.version === 'new' ? 'new' : 'classic';

  if (version === 'classic') {
    // L'invito c'e' solo dove si sceglie (o se la nuova per tutti lascia ancora tornare indietro)
    const snoozed = !!u.invite_snoozed_until && now.toISOString() < u.invite_snoozed_until;
    // Il promemoria dell'admin vale solo per chi non ha mai provato la nuova (chi e' tornato indietro ha gia' scelto)
    const reminded = !u.version && !!org?.reminder_at && (!u.invite_snoozed_until || org.reminder_at > shiftBack(u.invite_snoozed_until, SNOOZE_DAYS));
    const compact = (u.invite_snoozes ?? 0) >= SNOOZES_BEFORE_COMPACT && !reminded;
    const invite = snoozed && !reminded ? 'none' : compact ? 'compact' : 'full';
    return { version, invite, canGoBack: false, inTrial: false, showCheckin: false };
  }

  const since = u.version_at ? now.getTime() - Date.parse(u.version_at) : null;
  return {
    version,
    invite: 'none',
    canGoBack: backOpen,
    inTrial: u.version === 'new' && since != null && since < TRIAL_DAYS * DAY,
    showCheckin: u.version === 'new' && since != null && since >= CHECKIN_DAYS * DAY && !u.checkin_done,
  };
}

/** Giorno in cui era stato rimandato l'invito (scadenza meno i giorni del rinvio). */
function shiftBack(iso: string, days: number): string {
  return new Date(Date.parse(iso) - days * DAY).toISOString();
}

/** "Piu' tardi": nasconde l'invito per 7 giorni e conta i rinvii. */
export function snoozeInvite(u: UserSwitchPrefs | undefined, now: Date = new Date()): UserSwitchPrefs {
  return { invite_snoozed_until: new Date(now.getTime() + SNOOZE_DAYS * DAY).toISOString(), invite_snoozes: (u?.invite_snoozes ?? 0) + 1 };
}

/** Motivi del ritorno alla Dashboard di prima (vanno in user_feedback, type 'feature'). */
export const BACK_REASONS = ['Mi manca qualcosa', 'È più lenta', 'Non trovo le cose', 'Troppe informazioni', 'Preferivo com’era'] as const;
